// Pure gate-decision function for the shell-level auth gate.
//
// Extracted from <deck-app> so it is unit-testable without a DOM, a
// router, or a live Clerk instance. Given the resolved auth status and
// the current path, it returns exactly what the shell should render.
//
// Keeping this pure is deliberate: the shell's render() is otherwise a
// thin switch, and the entire security-relevant branching lives here
// where it can be exhaustively tested (see auth-gate.test.ts).

/** Resolved authentication status for the current visitor. */
export type AuthStatus = 'loading' | 'authed' | 'anon' | 'misconfigured';

/** What the shell should render for the current (status, path) pair. */
export type GateDecision = 'app' | 'splash' | 'login' | 'blocked' | 'redirect';

/**
 * Anonymous routes that ALWAYS render the outlet regardless of auth status —
 * they own their own chrome and serve users with no Ricoh account:
 *   - `/share/:token`     — anonymous share links are core to the product.
 *   - `/live/:sessionId`  — live viewers have no Ricoh account; presenting is
 *                           in-place from the studio (no separate presenter route).
 *   - `/accept`           — the magic-link invite landing. The invitee is NOT
 *                           signed in yet; the token in the URL is the
 *                           credential. They set a password here and are signed
 *                           straight in (their first login), so the page must
 *                           render without an auth gate.
 *
 * `/login` is deliberately NOT here — it is status-aware (see gateDecision):
 * an anonymous visitor gets the standalone, centred sign-in; an already-authed
 * visitor is bounced into the app. Everything else is protected, gated by status.
 */
export function isOpenRoute(path: string): boolean {
  return path.startsWith('/share/') || path.startsWith('/live/') || path === '/accept';
}

/**
 * Decide what the shell renders.
 *
 *   Open route     -> 'app'      (always, so anonymous share + login render)
 *   Protected +
 *     'loading'    -> 'splash'   (block protected paint until Clerk resolves)
 *     'misconfigured' -> 'blocked' (fail closed on a broken prod build)
 *     'anon'       -> 'login'    (redirect unauthenticated users to sign in)
 *     'authed'     -> 'app'      (render the protected outlet)
 */
export function gateDecision(status: AuthStatus, path: string): GateDecision {
  if (isOpenRoute(path)) return 'app';

  // /login is status-aware: an already-authed visitor has no sign-in to do and
  // is bounced into the app (the shell performs the redirect); an anonymous
  // visitor gets the standalone, centred sign-in. loading / misconfigured fall
  // through to the shared handling below (splash / blocked).
  if (path === '/login') {
    if (status === 'authed') return 'redirect';
    if (status === 'anon') return 'login';
  }

  switch (status) {
    case 'loading':
      return 'splash';
    case 'misconfigured':
      return 'blocked';
    case 'anon':
      return 'login';
    case 'authed':
      return 'app';
  }
}
